import _ from 'underscore';

import {EventConstants} from 'constants/EventConstants';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST, restToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import Syncer from 'graphql-client/sync/Syncer';
import InventoryFileStore from 'inventory/InventoryFileStore';
import AppDispatcher from 'lib/Dispatcher';

import {AliasesForBioentityGraphQL} from '../../../../graphql-fragments/registry/registry-items/__generated/AliasesForBioentityGraphQL';

export interface BioentityAliasInfosByBioentityID {
  [id: string]: Array<BioentityAliasInfo>;
}

export default class AliasesForBioentitySyncer extends Syncer<
  BioentityAliasInfosByBioentityID,
  AliasesForBioentityGraphQL
> {
  override shouldSubscribeToRedux = false;
  override stores = [InventoryFileStore];

  readFromStore(): {current: BioentityAliasInfosByBioentityID; equalityRef: unknown} {
    const baisByIdMap = InventoryFileStore.bioentityAliasInfosMap;
    const dictCopy: {[p: string]: Array<BioentityAliasInfo>} = {};
    for (const entry of [...baisByIdMap.entries()]) {
      const [id, bais] = entry;
      dictCopy[id] = bais;
    }
    return {current: dictCopy, equalityRef: {rawMap: baisByIdMap, mapCounter: baisByIdMap.getCounter()}};
  }

  writeToApolloCache(inputs: BioentityAliasInfosByBioentityID): void {
    // Backbone models may, in some circumstances, drop non-bioentity files into the store with a value of
    // "undefined" for bioentity alias infos (see loadInventoryFileInfo in
    // client/metadata/MetadataTabState.js).
    const nonNullBAIs = Object.entries(inputs).filter(([id, bais]) => bais != null);
    const gqlBAIsWithBioentity = nonNullBAIs.map(([bioentityId, bais]) =>
      restToGraphQL('AliasesForBioentity', {id: bioentityId, bioentityAliasInfos: bais})
    );
    writeFragmentsToCache(getFragmentWrapper('AliasesForBioentity'), gqlBAIsWithBioentity);
  }

  writeToStore(graphQLObjs: Array<AliasesForBioentityGraphQL>): void {
    const restBioentities = graphQLArrayToREST('AliasesForBioentity', graphQLObjs);
    // eslint-disable-next-line benchlint/no-spread-reduce
    const restBAIsByBioentityID = _.unique(restBioentities, (bioentity) => bioentity.id).reduce(
      (byId, bioentity) => ({
        ...byId,
        [bioentity.id]: bioentity.bioentityAliasInfos,
      }),
      {}
    );
    AppDispatcher.dispatch({
      actionType: EventConstants.BIOENTITY_ALIAS_INFO_LOAD_COMPLETED,
      bioentityAliasInfosById: restBAIsByBioentityID,
    });
  }
}
